import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Group, Loader, SegmentedControl, Table, Text, TextInput } from "@mantine/core";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const timeLabel = (value) =>
  new Date(value).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
const dayLabel = (date) =>
  new Date(`${date}T12:00:00+05:30`).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Asia/Kolkata",
  });

// Maid cafe (add-on) bookings: seats per slot by plan, online left, walk-in held, entries, late, no-show.
const CafeBookings = ({ productId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(null);
  const [search, setSearch] = useState("");
  const { showSuccess, showError } = useNotification();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/products/${productId}/addon-bookings?buyers=1`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setData(json.data);
      setDay((prev) => prev || json.data.days[0]?.date || null);
    } catch (e) {
      showError("Error", e.message || "Failed to load cafe bookings");
    } finally {
      setLoading(false);
    }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const current = useMemo(() => data?.days.find((d) => d.date === day), [data, day]);
  const q = search.trim().toLowerCase();
  const buyerRows = useMemo(() => {
    if (!current) return [];
    return current.slots.flatMap((s) =>
      (s.buyers || []).map((b) => ({ ...b, slot: timeLabel(s.startTime) })),
    );
  }, [current]);
  const matching = q
    ? buyerRows.filter((b) => [b.name, b.email, b.ticketNumber].some((v) => String(v || "").toLowerCase().includes(q)))
    : [];

  const release = async (slot) => {
    if (!window.confirm(`Release ${slot.walkInSeats} walk-in seat(s) of the ${timeLabel(slot.startTime)} session to online sale?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/products/timeslots/${slot.slotId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ releaseWalkInSeats: slot.walkInSeats }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed");
      showSuccess("Done", "Walk-in seats released to online sale");
      load();
    } catch (e) {
      showError("Error", e.message);
    }
  };

  const exportCsv = () => {
    const rows = [["Date", "Time", "Ticket", "Plan", "Status", "Name", "Email", "Late"]];
    data.days.forEach((d) =>
      d.slots.forEach((s) =>
        (s.buyers || []).forEach((b) =>
          rows.push([d.date, timeLabel(s.startTime), b.ticketNumber, b.plan, b.status, b.name, b.email, b.late ? "yes" : ""]),
        ),
      ),
    );
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "cafe-bookings.csv";
    a.click();
  };

  if (loading && !data) return <Loader size="sm" />;
  if (!data || data.days.length === 0) return <Text c="dimmed">No cafe sessions or bookings yet.</Text>;

  return (
    <Box data-testid="cafe-bookings">
      <Group justify="space-between" mb="md">
        <SegmentedControl
          value={day || ""}
          onChange={setDay}
          data={data.days.map((d) => ({ value: d.date, label: `${dayLabel(d.date)} (${d.seats} ${d.seats === 1 ? "seat" : "seats"})` }))}
        />
        <Group>
          <TextInput placeholder="Search buyer or ticket" value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
          <Button variant="light" onClick={exportCsv}>Export CSV</Button>
          <Button variant="subtle" onClick={load}>Refresh</Button>
        </Group>
      </Group>

      {q && (
        <Table mb="lg" withTableBorder data-testid="cafe-search-results">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Time</Table.Th><Table.Th>Ticket</Table.Th><Table.Th>Plan</Table.Th><Table.Th>Buyer</Table.Th><Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {matching.length === 0 ? (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No match on this day.</Text></Table.Td></Table.Tr>
            ) : (
              matching.map((b) => (
                <Table.Tr key={b.ticketNumber}>
                  <Table.Td>{b.slot}</Table.Td><Table.Td>{b.ticketNumber}</Table.Td><Table.Td>{b.plan}</Table.Td>
                  <Table.Td>{b.name || b.email}</Table.Td><Table.Td>{b.status}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      )}

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Session</Table.Th>
            {data.plans.map((p) => (
              <Table.Th key={p.id}>{p.title}{p.maxPerSlot ? ` (max ${p.maxPerSlot})` : ""}</Table.Th>
            ))}
            <Table.Th>Online left</Table.Th>
            <Table.Th>Walk-in held</Table.Th>
            <Table.Th>In / late / no-show</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(current?.slots || []).map((s) => (
            <Table.Tr key={s.slotId}>
              <Table.Td fw={600}>{timeLabel(s.startTime)}{!s.isActive && <Badge ml="xs" color="gray">Off</Badge>}</Table.Td>
              {s.byPlan.map((p) => (
                <Table.Td key={p.ticketTypeId}>
                  {p.sold}
                  {p.maxPerSlot ? `/${p.maxPerSlot}` : ""}
                </Table.Td>
              ))}
              <Table.Td>{s.onlineLeft}</Table.Td>
              <Table.Td>{s.walkInSeats}</Table.Td>
              <Table.Td>{s.entered} / {s.late} / {s.noShow}</Table.Td>
              <Table.Td>
                {s.walkInSeats > 0 && (
                  <Button size="compact-xs" variant="light" onClick={() => release(s)} data-testid={`release-${s.slotId}`}>
                    Release walk-in seats
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

export default CafeBookings;
